import {
  array,
  boolean,
  custom,
  email,
  type input,
  number,
  object,
  type output,
  string,
  url,
} from 'zod';
import type { EventAttendee } from '../event-attendee';
import type { Questionnaire } from '../questionnaire';

export const MAX_QUESTIONNAIRE_CONTACT_FIELD_LENGTH = 100;
export const MAX_EVENT_ATTENDEE_FIELD_LENGTH = 150;

const questionnaireWizardValidationInputSchema = object({
  questionnaire: custom<Questionnaire>(),
  attendingEventAttendees: array(custom<EventAttendee>()),
  attendingFoodEventAttendees: array(custom<EventAttendee>()),
});

export type QuestionnaireWizardValidationInput = input<typeof questionnaireWizardValidationInputSchema>;

const questionnaireContactFieldsValidationSchema = object({
  contactName: string().trim().min(1, 'Enter the contact name.').max(
    MAX_QUESTIONNAIRE_CONTACT_FIELD_LENGTH,
    `Do not use more than ${MAX_QUESTIONNAIRE_CONTACT_FIELD_LENGTH} characters for the contact name.`,
  ),
  companyName: string().trim().min(1, 'Enter the company name.').max(
    MAX_QUESTIONNAIRE_CONTACT_FIELD_LENGTH,
    `Do not use more than ${MAX_QUESTIONNAIRE_CONTACT_FIELD_LENGTH} characters for the company name.`,
  ),
  companyWebsite: string().trim().pipe(
    url({
      error: (issue) => issue.input === ''
        ? 'Enter the company website URL.'
        : 'Enter the company website URL in this format: https://example.com.',
    }).max(
      MAX_QUESTIONNAIRE_CONTACT_FIELD_LENGTH,
      `Do not use more than ${MAX_QUESTIONNAIRE_CONTACT_FIELD_LENGTH} characters for the company website URL.`,
    ),
  ),
});

export const questionnaireContactValidationSchema = questionnaireWizardValidationInputSchema
  .transform((input) => ({
    contactName: input.questionnaire.contact.contactName,
    companyName: input.questionnaire.contact.companyName,
    companyWebsite: input.questionnaire.contact.companyWebsite,
  }))
  .pipe(questionnaireContactFieldsValidationSchema);

export type QuestionnaireContactValidation = output<typeof questionnaireContactValidationSchema>;

export const eventAttendeeValidationSchema = object({
  eventAttendeeId: number(),
  questionnaireId: number(),
  eventId: number(),
  isAttending: boolean(),
  prefix: string(),
  firstName: string().trim().min(1, 'Enter the first name.').max(
    MAX_EVENT_ATTENDEE_FIELD_LENGTH,
    `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for the first name.`,
  ),
  lastName: string().trim().min(1, 'Enter the last name.').max(
    MAX_EVENT_ATTENDEE_FIELD_LENGTH,
    `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for the last name.`,
  ),
  title: string().max(
    MAX_EVENT_ATTENDEE_FIELD_LENGTH,
    `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for the title.`,
  ),
  emailAddress: string().trim().pipe(
    email({
      error: (issue) => issue.input === ''
        ? 'Enter the email address.'
        : 'Enter the email address in this format: name@example.com.',
    }).max(
      MAX_EVENT_ATTENDEE_FIELD_LENGTH,
      `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for the email address.`,
    ),
  ),
  primaryLanguage: string().max(
    MAX_EVENT_ATTENDEE_FIELD_LENGTH,
    `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for the primary language.`,
  ),
  dietaryRequests: string().max(
    MAX_EVENT_ATTENDEE_FIELD_LENGTH,
    `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for dietary requests.`,
  ),
  accessibilityNeeds: string().max(
    MAX_EVENT_ATTENDEE_FIELD_LENGTH,
    `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for accessibility needs.`,
  ),
  timeZone: string(),
});

export const foodEventAttendeeValidationSchema = eventAttendeeValidationSchema.extend({
  primaryLanguage: string().trim().min(1, 'Enter the primary language.').max(
    MAX_EVENT_ATTENDEE_FIELD_LENGTH,
    `Do not use more than ${MAX_EVENT_ATTENDEE_FIELD_LENGTH} characters for the primary language.`,
  ),
});

export type EventAttendeeValidation = output<typeof eventAttendeeValidationSchema>;

const questionnaireEventAttendeesFieldsValidationSchema = object({
  attendingEventAttendees: array(eventAttendeeValidationSchema),
  attendingFoodEventAttendees: array(foodEventAttendeeValidationSchema),
}).transform((input) => [
  ...input.attendingEventAttendees,
  ...input.attendingFoodEventAttendees,
]);

export const questionnaireEventAttendeesValidationSchema = questionnaireWizardValidationInputSchema
  .transform((input) => ({
    attendingEventAttendees: input.attendingEventAttendees,
    attendingFoodEventAttendees: input.attendingFoodEventAttendees,
  }))
  .pipe(questionnaireEventAttendeesFieldsValidationSchema);

export type QuestionnaireEventAttendeesValidation = output<typeof questionnaireEventAttendeesValidationSchema>;

export const questionnaireMediaValidationSchema = questionnaireWizardValidationInputSchema
  .transform((input) => ({
    mediaConsentDisclaimer: input.questionnaire.mediaConsentDisclaimer,
  }))
  // Zod infers a field schema that accepts undefined as an optional key,
  // which is incompatible with this required projection.
  .refine((input) => input.mediaConsentDisclaimer === true, {
    error: 'Select the media consent checkbox.',
    path: ['mediaConsentDisclaimer'],
  });

export type QuestionnaireMediaValidation = output<typeof questionnaireMediaValidationSchema>;
